import { AbstractControl, UntypedFormControl } from '@angular/forms'
import { FormlyFieldConfig } from '@ngx-formly/core'

export const inputWithSelector = {
  expression: (c) => {
    if (c.value && c.value.inputValue && c.value.inputValue.length > 0) {
      return true
    }
    return false
  },
  message: (error, field: FormlyFieldConfig) => 'Input is required',
}
export const numeric = {
  expression: (c: AbstractControl, field: FormlyFieldConfig) => {
    const { numberType, required } = field.props
    if (
      !required &&
      (c.value === null || c.value === undefined || c.value === '')
    ) {
      return true
    }
    if (numberType && isNaN(c.value)) {
      return false
    }
    return true
  },
  message: (error, field: FormlyFieldConfig) => {
    return 'Invalid Number'
  },
}
export const minmax = {
  expression: (c: AbstractControl, field: FormlyFieldConfig) => {
    const { min, max, required } = field.props
    if (
      !required &&
      (c.value === null || c.value === undefined || c.value === '')
    ) {
      return true
    }
    if (min !== undefined && c.value < min) {
      return false
    }
    if (max !== undefined && c.value > max) {
      return false
    }
    return true
  },
  message: (error, field: FormlyFieldConfig) => {
    const { min, max } = field.props
    if (min !== undefined && field.formControl.value < min) {
      return `Value should be greater than or equal to ${min}`
    }
    if (max !== undefined && field.formControl.value > max) {
      return `Value should be less than or equal to ${max}`
    }
    return ''
  },
}
export const alphaOnly = {
  expression: (c) => !c.value || /^[a-zA-Z ]*$/.test(c.value),
  message: (error, field: FormlyFieldConfig) => 'No Numerical Values',
}
export const phone = {
  expression: (c) => {
    return !c.value || /^\d{10}$/.test(c.value)
  },
  message: (error, field: FormlyFieldConfig) => 'Not a Valid Phone Number',
}
export const postalCode = {
  expression: (c) => !c.value || /^\d{5}$/.test(c.value),
  message: (error, field: FormlyFieldConfig) => 'Not a Valid Zip Code',
}
export const timeframe = {
  expression: (c: AbstractControl) => {
    if (c.value == undefined || c.value == null) return true
    else return c.value >= 1
  },
  message: (error, field: FormlyFieldConfig) =>
    'Value should be greater than 0',
}

export const numericRange = {
  expression: (c: AbstractControl, field: FormlyFieldConfig) => {
    const { validRanges } = field.props
    let stringValue = field.formControl.value
    if (!stringValue) {
      return true
    }
    const inputNumbers = numericRange.flattenInputRanges(stringValue)
    if (inputNumbers === false) {
      return false
    }
    const validNumbers = numericRange.parseValidRanges(validRanges)
    const invalidNumbers = inputNumbers.filter(
      (num) => !validNumbers.includes(num)
    )
    return invalidNumbers.length === 0
  },

  message: (error, field: FormlyFieldConfig) => {
    const { validationMessages, validRanges } = field.props
    let stringValue = field.formControl.value
    if (!stringValue) {
      return true
    }
    const inputNumbers = numericRange.flattenInputRanges(stringValue)
    if (inputNumbers === false) {
      return (
        validationMessages['invalidFormat'] ||
        'Invalid range input format X-Y, X must be greater than Y'
      )
    }
    const validNumbers = numericRange.parseValidRanges(validRanges)
    const invalidNumbers = inputNumbers.filter(
      (num) => !validNumbers.includes(num)
    )
    if (invalidNumbers.length > 0) {
      return (
        validationMessages['invalidFormat'] ||
        `The number(s) you entered fall outside the valid ranges. Please enter numbers within the ranges: ${validRanges}`
      )
    }
    return true
  },

  // Helper functions
  parseValidRanges(validRanges: string): number[] {
    const targetRangesArray = validRanges
      .split(',')
      .map((range) => range.trim())
      .reduce<number[]>((acc, range) => {
        // Handle the case where it's a single number (not a range)
        if (!range.includes('-')) {
          const num = Number(range)
          if (!isNaN(num)) acc.push(num)
        } else {
          // Otherwise, parse the range (e.g., "1-5")
          const parsedRange = numericRange.parseRange(range)
          if (parsedRange) acc.push(...parsedRange) // Only add valid ranges
        }
        return acc
      }, [])

    return targetRangesArray
  },

  parseRange(range: string): number[] | null {
    const [start, end] = range.split('-').map(Number)
    // Check if the range format is valid (X < Y)
    if (isNaN(start) || isNaN(end)) {
      return null // Invalid range if start or end is not a valid number
    }
    // Check that start is less than end
    if (start >= end) {
      return null // Invalid range if start is greater than or equal to end
    }
    // If it's a valid range, return an array of numbers from start to end
    const numbersInRange: number[] = []
    for (let i = start; i <= end; i++) {
      numbersInRange.push(i)
    }
    return numbersInRange
  },

  flattenInputRanges(
    inputRange: string | string[] | number[]
  ): number[] | false {
    let inputNumbers: number[] = []
    // Handle null or empty input (allow empty input as valid)
    if (!inputRange) {
      return inputNumbers // If the input is null or empty, return an empty array
    }
    if (typeof inputRange === 'string') {
      inputNumbers = this.handleInputRangeString(inputRange)
    } else if (Array.isArray(inputRange)) {
      inputNumbers = this.handleInputRangeArray(inputRange)
    }
    // After processing all ranges/numbers, check for NaN in the result
    if (inputNumbers.some(isNaN)) {
      return false // If NaN exists, return false to indicate invalid input format
    }
    return inputNumbers
  },

  handleInputRangeString(inputRange: string) {
    // If it's a single range string (e.g., "1-5")
    const inputArray = inputRange.split(',')
    const inputNumbers: number[] = []
    for (let item of inputArray) {
      const trimmedItem = item.trim()
      if (trimmedItem.includes('-')) {
        // If it's a range string (e.g., "1-5")
        const [start, end] = trimmedItem.split('-').map(Number)
        if (isNaN(start) || isNaN(end) || start >= end) {
          return false // Invalid range, return false immediately
        }
        for (let i = start; i <= end; i++) {
          inputNumbers.push(i)
        }
      } else {
        // If it's a single number string (e.g., "8")
        const num = Number(trimmedItem)
        if (isNaN(num)) {
          return false // Invalid number, return false immediately
        }
        inputNumbers.push(num) // Add single numbers directly
      }
    }
    return inputNumbers
  },

  handleInputRangeArray(inputRange: (string | number)[]): number[] | false {
    const inputNumbers: number[] = []

    for (const item of inputRange) {
      if (typeof item === 'number') {
        inputNumbers.push(item)
        continue
      }

      const result = this.processInputItem(item)
      if (result === false) return false
      inputNumbers.push(...result)
    }

    return inputNumbers
  },

  processInputItem(item: string): number[] | false {
    if (item.includes('-')) {
      // If the item is a range string (e.g., "1-5"), process as a range
      return this.processRange(item)
    }

    // If the item is a single number string (e.g., "8"), process as a number
    const num = Number(item)
    return isNaN(num) ? false : [num]
  },

  processRange(range: string): number[] | false {
    // Extract start and end numbers from the range
    const [start, end] = range.split('-').map(Number)
    // Validate the range values
    if (isNaN(start) || isNaN(end) || start >= end) return false // Invalid range, return false immediately

    // Generate an array of numbers from start to end
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  },
}

export const file = {
  expression: (control: UntypedFormControl) => {
    const validExtensions = ['.jpg', '.jpeg', '.tif', '.tiff', '.png']
    const fileSizeLimit = 5000000 // 5 MB

    const files = control.value

    if (files != null) {
      // intentional for loop - only want to iterate as many times as necessary
      for (const f of files) {
        const extension = f.name
          .substring(f.name.lastIndexOf('.'))
          .toLowerCase()

        if (!validExtensions.includes(extension) || f.size > fileSizeLimit) {
          return false
        }
      }
    }

    // the user has not selected a file (this is a valid state)
    return true
  },
  message: (err, field: FormlyFieldConfig) => {
    const validExtensions = ['.jpg', '.jpeg', '.tif', '.tiff', '.png']
    const fileSizeLimit = 1000000 // 1 MB

    const files = [...field.formControl.value]

    const anyInvalidFileType = files.some(
      (f) =>
        !validExtensions.includes(
          f.name.substring(f.name.lastIndexOf('.')).toLowerCase()
        )
    )
    const anyInvalidFileSize = files.some((f) => f.size > fileSizeLimit)

    if (anyInvalidFileType) {
      return 'Only image files are permitted'
    } else if (anyInvalidFileSize) {
      return 'Attachments must be 1 MB or less'
    } else {
      return ''
    }
  },
}
